// ============================================================================
// WHATSAPP ADMIN PAGE — Panel multi-centro (ADMIN) / single-centro (GESTOR)
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { WifiOff, QrCode, RefreshCw, Loader2, Phone, Clock, CheckCircle, AlertCircle, X, Save } from 'lucide-react';
import { whatsappApi, type WhatsappStatusDto } from '../../api/whatsapp';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CONFIG = {
  CONNECTED:    { label: 'Conectado',      bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle },
  QR_PENDING:   { label: 'Esperando QR',   bg: 'bg-amber-100',  text: 'text-amber-700',  icon: QrCode },
  CONNECTING:   { label: 'Sincronizando',  bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Loader2 },
  DISCONNECTED: { label: 'Desconectado',   bg: 'bg-gray-100',   text: 'text-gray-600',   icon: WifiOff },
  FAILED:       { label: 'Error',          bg: 'bg-red-100',    text: 'text-red-700',    icon: AlertCircle },
} as const;

function formatTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.DISCONNECTED;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'CONNECTING' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

// ── Modal QR con feedback de sincronización ───────────────────────────────────
function QRModal({ siteId, siteName, isOpen, onClose, onConnected, isAdmin }: {
  siteId: string; siteName: string; isOpen: boolean;
  onClose: () => void; onConnected: () => void; isAdmin: boolean;
}) {
  const toast = useToast();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('QR_PENDING');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      if (pollRef.current) clearInterval(pollRef.current);
      setQrDataUrl(null);
      setCurrentStatus('QR_PENDING');
      return;
    }

    const poll = async () => {
      try {
        const data = isAdmin
          ? await whatsappApi.getQRForSite(siteId)
          : await whatsappApi.getQR();

        // QR desaparece → usuario escaneó → feedback inmediato
        if (!data.qrDataUrl && data.status !== 'CONNECTED') {
          setQrDataUrl(null);
          setCurrentStatus('CONNECTING');
          // Acelerar polling para detectar CONNECTED rápido
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = setInterval(poll, 1000);
          }
        } else if (data.qrDataUrl) {
          setQrDataUrl(data.qrDataUrl);
          setCurrentStatus(data.status);
        }

        if (data.status === 'CONNECTED') {
          if (pollRef.current) clearInterval(pollRef.current);
          onConnected();
          toast.success('WhatsApp conectado', `Sesión vinculada correctamente a ${siteName}`);
          onClose();
        }
      } catch { /* ignorar */ }
    };

    poll();
    pollRef.current = setInterval(poll, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isOpen, siteId]);

  if (!isOpen) return null;

  const isConnecting = currentStatus === 'CONNECTING';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={!isConnecting ? onClose : undefined} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
        {!isConnecting && (
          <button onClick={onClose}
            className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}

        {isConnecting ? (
          /* ── Estado sincronizando ── */
          <div className="py-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Sincronizando...</h3>
            <p className="text-sm text-gray-500">
              Conectando la sesión de WhatsApp para <strong>{siteName}</strong>.
              Por favor espera unos segundos.
            </p>
          </div>
        ) : (
          /* ── Estado QR ── */
          <>
            <div className="w-10 h-10 bg-[#008DD0]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <QrCode className="h-5 w-5 text-[#008DD0]" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Escanea el código QR</h3>
            <p className="text-xs text-gray-500 mb-1">
              Vinculando a: <strong>{siteName}</strong>
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo
            </p>
            {qrDataUrl ? (
              <div className="flex items-center justify-center">
                <img src={qrDataUrl} alt="WhatsApp QR" className="w-56 h-56 rounded-xl border border-gray-200" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
                <p className="text-sm text-gray-400">Generando QR...</p>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-4">El QR se actualiza automáticamente</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Tarjeta de centro ────────────────────────────────────────────────────────
function SiteCard({ siteStatus, sendHour, sendMinute, isAdmin, onRefresh }: {
  siteStatus: WhatsappStatusDto;
  sendHour: number;
  sendMinute: number;
  isAdmin: boolean;
  onRefresh: () => void;
}) {
  const toast = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [localSendHour, setLocalSendHour] = useState(sendHour);
  const [localSendMinute, setLocalSendMinute] = useState(sendMinute);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const isConnected = siteStatus.status === 'CONNECTED';

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      if (isAdmin) {
        await whatsappApi.connectSite(siteStatus.siteId);
      } else {
        await whatsappApi.connect();
      }
      setShowQR(true);
    } catch {
      toast.error('Error', 'No se pudo iniciar la conexión');
    } finally { setIsConnecting(false); }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      if (isAdmin) {
        await whatsappApi.disconnectSite(siteStatus.siteId);
      } else {
        await whatsappApi.disconnect();
      }
      toast.success('Desconectado', 'La sesión de WhatsApp se ha cerrado');
      onRefresh();
    } catch {
      toast.error('Error', 'No se pudo desconectar');
    } finally { setIsDisconnecting(false); }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await whatsappApi.updateSettingsForSite(siteStatus.siteId, localSendHour, localSendMinute);
      toast.success('Guardado', `Recordatorios a las ${formatTime(localSendHour, localSendMinute)}`);
    } catch {
      toast.error('Error', 'No se pudo guardar la configuración');
    } finally { setIsSavingSettings(false); }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">{siteStatus.siteName || 'Centro'}</p>
            {siteStatus.siteCode && <p className="text-xs text-gray-400 font-mono">{siteStatus.siteCode}</p>}
          </div>
          <StatusBadge status={siteStatus.status} />
        </div>

        {/* Número conectado */}
        {isConnected && siteStatus.phoneNumber && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl border border-green-100">
            <Phone className="h-4 w-4 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">+{siteStatus.phoneNumber}</p>
              {siteStatus.displayName && <p className="text-xs text-green-600">{siteStatus.displayName}</p>}
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="flex items-center gap-2">
          {!isConnected && (
            <button onClick={handleConnect} disabled={isConnecting}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg disabled:opacity-50 transition-colors">
              {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
              {isConnecting ? 'Iniciando...' : 'Conectar con QR'}
            </button>
          )}
          {isConnected && (
            <button onClick={handleDisconnect} disabled={isDisconnecting}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
              {isDisconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <WifiOff className="h-3.5 w-3.5" />}
              Desconectar
            </button>
          )}
        </div>

        {/* Hora de envío */}
        <div className="border-t border-gray-100 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">Hora de envío de recordatorios</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" min={0} max={23} value={localSendHour}
              onChange={e => setLocalSendHour(Math.min(23, Math.max(0, Number(e.target.value))))}
              className="w-14 px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
            <span className="text-gray-400 text-xs">:</span>
            <input type="number" min={0} max={59} step={1} value={localSendMinute}
              onChange={e => setLocalSendMinute(Math.min(59, Math.max(0, Number(e.target.value))))}
              className="w-14 px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
            <span className="text-xs font-semibold text-gray-700">{formatTime(localSendHour, localSendMinute)}</span>
            <button onClick={handleSaveSettings} disabled={isSavingSettings}
              className="ml-auto flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg disabled:opacity-50 transition-colors">
              {isSavingSettings ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Guardar
            </button>
          </div>
        </div>
      </div>

      <QRModal
        siteId={siteStatus.siteId}
        siteName={siteStatus.siteName || 'Centro'}
        isOpen={showQR}
        onClose={() => setShowQR(false)}
        onConnected={onRefresh}
        isAdmin={isAdmin}
      />
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export function WhatsAppAdminPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'ADMIN';

  const [siteStatuses, setSiteStatuses] = useState<WhatsappStatusDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isAdmin) {
        const data = await whatsappApi.getAllStatus();
        setSiteStatuses(data);
      } else {
        const data = await whatsappApi.getStatus();
        setSiteStatuses([data]);
      }
    } catch {
      toast.error('Error', 'No se pudo cargar el estado de WhatsApp');
    } finally { setIsLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin
              ? 'Gestiona las sesiones de WhatsApp de cada centro para el envío de recordatorios.'
              : 'Gestiona la sesión de WhatsApp de este centro para el envío de recordatorios.'}
          </p>
        </div>
        <button onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
          <RefreshCw className="h-4 w-4" /> Actualizar
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
        </div>
      ) : (
        <div className="space-y-4">
          {siteStatuses.map(s => (
            <SiteCard
              key={s.siteId}
              siteStatus={s}
              sendHour={s.sendHour}
              sendMinute={s.sendMinute}
              isAdmin={isAdmin}
              onRefresh={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default WhatsAppAdminPage;
